import type { SupabaseClient } from '@supabase/supabase-js';
import { DOCUMENT_BUCKET } from './constants';
import {
  buildStoragePath,
  newDocumentId,
  resolveMimeType,
  validateDocumentFile,
} from './file-helpers';
import {
  DocumentError,
  documentFromRow,
  type DocumentRow,
  type DocumentTarget,
  type RawDocumentRow,
} from './types';

/**
 * Colonnes lues + noms joints. `documents` a DEUX FK vers `profiles`
 * (`uploaded_by`, `peer_id`) : chaque embed est désambiguïsé par le nom de FK,
 * sinon PostgREST renvoie PGRST201 (HTTP 300) et la liste reste vide sans erreur.
 * Un embed refusé par la RLS (`profiles`) renvoie `null`, jamais une ligne en moins.
 */
const SELECT_COLUMNS =
  'id, uploaded_by, scope, peer_id, class_id, room_id, discussion_id, file_name, mime_type, size_bytes, storage_path, created_at, ' +
  'uploader:profiles!documents_uploaded_by_fkey(display_name), ' +
  'peer:profiles!documents_peer_id_fkey(display_name), ' +
  'class:classes(name), ' +
  'room:video_rooms(title, starts_at), ' +
  'discussion:discussions(title)';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function assertUuid(value: string): void {
  if (!UUID_RE.test(value)) {
    throw new DocumentError('list_failed', 'Identifiant invalide.');
  }
}

export interface UploadInput {
  file: File;
  target: DocumentTarget;
  /** Auteur du dépôt (= `auth.uid()`), imposé aussi par la RLS. */
  userId: string;
}

export interface DocumentsClient {
  /** Fichiers d'une cible. Contact : dans les DEUX sens (moi→lui, lui→moi). */
  list(target: DocumentTarget, userId: string): Promise<DocumentRow[]>;
  /** Tout ce que la RLS laisse voir à l'appelant (page « Mes fichiers »). */
  listVisible(limit?: number): Promise<DocumentRow[]>;
  /** Un document, ou `null` s'il est supprimé ou invisible pour l'appelant (RLS). */
  getById(id: string): Promise<DocumentRow | null>;
  /** Upload Storage puis insert de la ligne ; rollback du binaire si l'insert échoue. */
  upload(input: UploadInput): Promise<DocumentRow>;
  signedUrl(
    storagePath: string,
    options?: { ttlSeconds?: number; downloadAs?: string },
  ): Promise<string>;
  /** Storage d'abord, puis la ligne (le SQL ne peut pas supprimer d'objet Storage). */
  remove(doc: Pick<DocumentRow, 'id' | 'storagePath'>): Promise<void>;
}

function targetColumns(target: DocumentTarget): Record<string, string> {
  switch (target.kind) {
    case 'contact':
      return { scope: 'contact', peer_id: target.peerId };
    case 'class':
      return { scope: 'class', class_id: target.classId };
    case 'room':
      return { scope: 'room', room_id: target.roomId };
    case 'discussion':
      return { scope: 'discussion', discussion_id: target.discussionId };
  }
}

/**
 * Client de documents. Le client Supabase est INJECTÉ (`db`) — aucun singleton :
 * `apps/app`, `apps/tutor` (invités) et `packages/visio` ont chacun le leur.
 * Les invités anonymes n'utilisent que `signedUrl` et `list` (lecture seule).
 */
export function createDocumentsClient(db: SupabaseClient): DocumentsClient {
  const storage = () => db.storage.from(DOCUMENT_BUCKET);

  return {
    async list(target, userId) {
      let query = db
        .from('documents')
        .select(SELECT_COLUMNS)
        .order('created_at', { ascending: false });
      if (target.kind === 'contact') {
        assertUuid(target.peerId);
        assertUuid(userId);
        query = query
          .eq('scope', 'contact')
          .or(
            `and(uploaded_by.eq.${userId},peer_id.eq.${target.peerId}),` +
              `and(uploaded_by.eq.${target.peerId},peer_id.eq.${userId})`,
          );
      } else if (target.kind === 'class') {
        query = query.eq('scope', 'class').eq('class_id', target.classId);
      } else if (target.kind === 'room') {
        query = query.eq('scope', 'room').eq('room_id', target.roomId);
      } else {
        query = query.eq('scope', 'discussion').eq('discussion_id', target.discussionId);
      }
      const { data, error } = await query;
      if (error) throw new DocumentError('list_failed', 'Impossible de charger les fichiers.');
      return ((data ?? []) as unknown as RawDocumentRow[]).map(documentFromRow);
    },

    async listVisible(limit = 500) {
      const { data, error } = await db
        .from('documents')
        .select(SELECT_COLUMNS)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw new DocumentError('list_failed', 'Impossible de charger les fichiers.');
      return ((data ?? []) as unknown as RawDocumentRow[]).map(documentFromRow);
    },

    async getById(id) {
      const { data, error } = await db
        .from('documents')
        .select(SELECT_COLUMNS)
        .eq('id', id)
        .maybeSingle();
      if (error) throw new DocumentError('list_failed', 'Impossible de charger ce fichier.');
      return data ? documentFromRow(data as unknown as RawDocumentRow) : null;
    },

    async upload({ file, target, userId }) {
      const problem = validateDocumentFile(file);
      if (problem) throw new DocumentError('invalid_file', problem);
      const mimeType = resolveMimeType(file) as string;

      const id = newDocumentId();
      const storagePath = buildStoragePath(userId, id, file.name);

      const { error: uploadError } = await storage().upload(storagePath, file, {
        contentType: mimeType,
        upsert: false,
      });
      if (uploadError) {
        throw new DocumentError('upload_failed', "Le fichier n'a pas pu être envoyé. Réessaie.");
      }

      const { data, error } = await db
        .from('documents')
        .insert({
          id,
          uploaded_by: userId,
          ...targetColumns(target),
          file_name: file.name.slice(0, 255),
          mime_type: mimeType,
          size_bytes: file.size,
          storage_path: storagePath,
        })
        .select(SELECT_COLUMNS)
        .single();

      if (error || !data) {
        // Rollback : ne pas laisser un binaire orphelin (meilleur effort).
        try {
          await storage().remove([storagePath]);
        } catch {
          /* le nettoyage est best-effort */
        }
        throw new DocumentError(
          'save_failed',
          "Le fichier n'a pas pu être enregistré : tu n'as peut-être plus accès à ce contact.",
        );
      }
      return documentFromRow(data as unknown as RawDocumentRow);
    },

    async signedUrl(storagePath, options) {
      const { data, error } = await storage().createSignedUrl(
        storagePath,
        options?.ttlSeconds ?? 300,
        options?.downloadAs ? { download: options.downloadAs } : undefined,
      );
      if (error || !data?.signedUrl) {
        throw new DocumentError('sign_failed', "Impossible d'ouvrir ce fichier.");
      }
      return data.signedUrl;
    },

    async remove(doc) {
      const { error: storageError } = await storage().remove([doc.storagePath]);
      if (storageError) throw new DocumentError('delete_failed', 'Suppression impossible.');

      const { data, error } = await db.from('documents').delete().eq('id', doc.id).select('id');
      if (error || !data || data.length === 0) {
        throw new DocumentError(
          'delete_failed',
          "Suppression impossible : tu n'as pas les droits sur ce fichier.",
        );
      }
    },
  };
}
